import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { auth as firebaseAuth } from './firebase';
import './App.css';
import './styles/utilities.css';

// Import utility functions
import {
  generateStableKey,
  generateStableListKey,
  getLocalCache,
  saveLocalCache,
  getPendingCount
} from './utils/helpers';
import { get, putJson } from './utils/apiClient';

// Import hooks
import useAuth from './hooks/useAuth';
import useListOperations from './hooks/useListOperations';
import useItemOperations from './hooks/useItemOperations';
import useFirebaseListeners from './hooks/useFirebaseListeners';
import useConnectivity from './hooks/useConnectivity';
import useOfflineQueue from './hooks/useOfflineQueue';
import useSharing from './hooks/useSharing';

// Import components
import Notification from './components/Notification';
import AuthPage from './components/AuthPage';
import AccountSettings from './components/AccountSettings';
import Dashboard from './components/Dashboard';
import ListDetailView from './components/ListDetailView';
import LandingPage from './components/LandingPage';
import EnvironmentToggle from './components/EnvironmentToggle';

// ----------------------------------------------------------------------------
// Axios global error handling.
// On 401/403: try one token refresh from Firebase Auth and retry the request.
// We do NOT auto-logout here — that caused offline boots to wipe local state and
// flicker users back to the landing page. Stale-token failures will surface via
// per-request error handling, and explicit logout still works from settings.
// ----------------------------------------------------------------------------
axios.interceptors.response.use(
  (res) => res,
  async (err) => {
    if (err.response) {
      console.error(`API Error: ${err.response.status} ${err.config?.url}`, err.response.data);
      const status = err.response.status;
      if ((status === 401 || status === 403) && !err.config.__retriedAfterRefresh) {
        try {
          const fbUser = firebaseAuth.currentUser;
          if (fbUser) {
            const freshToken = await fbUser.getIdToken(true);
            localStorage.setItem('kanto-token', freshToken);
            err.config.headers = err.config.headers || {};
            err.config.headers.Authorization = `Bearer ${freshToken}`;
            err.config.__retriedAfterRefresh = true;
            return axios(err.config);
          }
        } catch (_) {}
      }
    } else {
      console.error('Network error:', err.message);
    }
    return Promise.reject(err);
  }
);

function App() {
  // Core state
  const [cache, setCache] = useState(getLocalCache);
  const [selectedListId, setSelectedListId] = useState(localStorage.getItem('kanto-selected-list'));
  const [showAccountSettings, setShowAccountSettings] = useState(false);
  const [accountError, setAccountError] = useState(null);
  const [accountSuccess, setAccountSuccess] = useState(null);
  const [activeTranslation, setActiveTranslation] = useState(null);
  const [aiStatusNotification, setAiStatusNotification] = useState(null);
  const [notification, setNotification] = useState(null);
  const [showCustomPromptModal, setShowCustomPromptModal] = useState(false);
  const [highlightedItems, setHighlightedItems] = useState(new Set());
  const [showAuthPage, setShowAuthPage] = useState(false);
  const [theme, setTheme] = useState(() => {
    const storedTheme = localStorage.getItem('kanto-theme');
    if (storedTheme) return storedTheme;
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  });

  // Auth hook
  const {
    token,
    user,
    authError,
    isFirebaseAuthenticated,
    authResolved,
    handleLogin,
    handleRegister,
    handleLogout: baseHandleLogout,
    handleForgotPassword,
    updateUser,
    setAuthError
  } = useAuth();

  // Connectivity hook
  const { isOnline, lastOnlineAt } = useConnectivity();

  // Enhanced logout that clears local state
  const handleLogout = useCallback(() => {
    baseHandleLogout();
    setSelectedListId(null);
    setCache({ lists: [], items: {}, pendingChanges: {}, history: [] });
  }, [baseHandleLogout]);

  // Notification helper
  const showNotification = useCallback((message, type = 'info') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 5000);
  }, []);

  // Sharing hook
  const {
    shareInvitation,
    listShares,
    showShareModal,
    setShowShareModal,
    linkCopied,
    fetchListShares,
    handleManageAccess,
    getShareLinkForList,
    handleCopyLinkForList,
    canUserShare
  } = useSharing({ token, user, setCache, setSelectedListId });

  // List operations hook
  const {
    handleCreateList,
    handleDeleteList,
    handleUpdateListVisibility,
    handleUpdateCustomPrompts,
    handleProcessItemsWithPrompts,
    handleReorderLists,
  } = useListOperations({ setCache, setSelectedListId, isOnline, showNotification });

  // Item operations hook
  const {
    handleCreateItem,
    handleUpdateItem,
    handleRenameItem,
    handleDeleteItem,
    handleClearCompleted,
    handleAiSort,
    handleAddProperties,
    handleTranslateItems,
    handleDeleteFromHistory,
    handleExpandSubitems,
    handleCollapseSubitems,
    handleToggleSubitem,
  } = useItemOperations({ cache, setCache, showNotification, setHighlightedItems, isOnline });

  // Firebase listeners hook
  useFirebaseListeners({
    isFirebaseAuthenticated,
    user,
    cache,
    setCache,
    updateUser,
    setAiStatusNotification,
    isOnline
  });

  // Offline queue processing
  useOfflineQueue({ isOnline, cache, setCache, showNotification });

  // Pending count for UI
  const pendingCount = useMemo(() => getPendingCount(cache), [cache]);

  // Fetch shares for owned lists when lists change
  useEffect(() => {
    if (!token || !user) return;
    const ownedLists = cache.lists.filter(list => list.isOwner);
    ownedLists.forEach(list => {
      if (!listShares[list.id]) {
        fetchListShares(list.id);
      }
    });
  }, [cache.lists, token, user, listShares, fetchListShares]);

  // REST fallback for lists if Firebase isn't ready
  useEffect(() => {
    if (!token || cache.lists.length > 0 || !isOnline) return;
    (async () => {
      try {
        const { data: listsData } = await get(`/lists`);
        const listsWithKeys = listsData.map(list => ({
          ...list,
          stableKey: generateStableListKey(list)
        }));
        setCache(c => ({ ...c, lists: listsWithKeys }));
      } catch (err) {
        console.error('REST lists fetch failed', err);
      }
    })();
  }, [token, cache.lists.length, isOnline]);

  // REST fallback for items if Firebase isn't ready
  useEffect(() => {
    if (!token || !selectedListId || !isOnline) return;
    if (isFirebaseAuthenticated) return;
    if (cache.items[selectedListId]?.length > 0) return;

    (async () => {
      try {
        const { data: itemsData } = await get(`/lists/${selectedListId}/items`);
        const itemsWithKeys = itemsData.map(item => ({
          ...item,
          stableKey: generateStableKey(item)
        }));
        setCache(c => ({
          ...c,
          items: { ...c.items, [selectedListId]: itemsWithKeys }
        }));
      } catch (err) {
        console.error('REST items fetch failed', err);
      }
    })();
  }, [token, selectedListId, isFirebaseAuthenticated, cache.items, isOnline]);

  // Persist cache to localStorage
  useEffect(() => { saveLocalCache(cache); }, [cache]);

  // Theme management
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e) => {
      if (!localStorage.getItem('kanto-theme')) {
        setTheme(e.matches ? 'dark' : 'light');
      }
    };
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Persist selected list
  useEffect(() => {
    selectedListId
      ? localStorage.setItem('kanto-selected-list', selectedListId)
      : localStorage.removeItem('kanto-selected-list');
  }, [selectedListId]);

  // Auto-clear AI status notification
  useEffect(() => {
    if (aiStatusNotification) {
      const timer = setTimeout(() => setAiStatusNotification(null), aiStatusNotification.duration);
      return () => clearTimeout(timer);
    }
  }, [aiStatusNotification]);

  // Account settings handlers
  const handleUpdateProfile = async (profileData) => {
    setAccountError(null);
    setAccountSuccess(null);
    try {
      const { data } = await putJson(`/auth/profile`, profileData);
      const updatedUser = { ...user, ...data.user };
      localStorage.setItem('kanto-user', JSON.stringify(updatedUser));
      updateUser(updatedUser);
      setAccountSuccess('Profile updated successfully!');
      setTimeout(() => setAccountSuccess(null), 3000);
    } catch (error) {
      console.error('Profile update failed:', error);
      setAccountError(error.response?.data?.message || 'Failed to update profile');
    }
  };

  const handleChangePassword = async (currentPassword, newPassword) => {
    setAccountError(null);
    setAccountSuccess(null);
    try {
      await putJson(`/auth/password`, { currentPassword, newPassword });
      setAccountSuccess('Password changed successfully!');
      setTimeout(() => setAccountSuccess(null), 3000);
    } catch (error) {
      console.error('Password change failed:', error);
      setAccountError(error.response?.data?.message || 'Failed to change password');
    }
  };

  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    localStorage.setItem('kanto-theme', newTheme);
    setTheme(newTheme);
  };

  // Computed values
  const selectedList = useMemo(
    () => cache.lists.find(l => l.id === selectedListId),
    [cache.lists, selectedListId]
  );

  // Render content based on state
  const renderContent = () => {
    if (!token) {
      // Show auth page if user clicked login, otherwise show landing page
      if (showAuthPage) {
        return (
          <AuthPage
            onLogin={handleLogin}
            onRegister={handleRegister}
            onForgotPassword={handleForgotPassword}
            error={authError}
            clearError={() => setAuthError(null)}
            shareInvitation={shareInvitation}
            onBack={() => setShowAuthPage(false)}
          />
        );
      }
      return <LandingPage onLogin={() => setShowAuthPage(true)} />;
    }

    if (showAccountSettings) {
      return (
        <AccountSettings
          user={user}
          onBack={() => setShowAccountSettings(false)}
          onUpdateProfile={handleUpdateProfile}
          onChangePassword={handleChangePassword}
          error={accountError}
          success={accountSuccess}
          onLogout={handleLogout}
          theme={theme}
          onToggleTheme={toggleTheme}
        />
      );
    }

    if (selectedList) {
      return (
        <ListDetailView
          user={user}
          list={selectedList}
          items={cache.items[selectedList.id] || []}
          history={cache.history || []}
          onBack={() => setSelectedListId(null)}
          onCreateItem={handleCreateItem}
          onUpdateItem={handleUpdateItem}
          onDeleteItem={handleDeleteItem}
          onClearCompleted={handleClearCompleted}
          onAiSort={handleAiSort}
          onDeleteFromHistory={handleDeleteFromHistory}
          onAddProperties={handleAddProperties}
          onTranslateItems={handleTranslateItems}
          onExpandSubitems={handleExpandSubitems}
          onCollapseSubitems={handleCollapseSubitems}
          onToggleSubitem={handleToggleSubitem}
          onUpdateListVisibility={handleUpdateListVisibility}
          onUpdateCustomPrompts={handleUpdateCustomPrompts}
          onProcessItemsWithPrompts={handleProcessItemsWithPrompts}
          activeTranslation={activeTranslation}
          showShareModal={showShareModal}
          setShowShareModal={setShowShareModal}
          handleCopyLink={handleCopyLinkForList}
          linkCopied={linkCopied}
          getShareLink={getShareLinkForList}
          setCache={setCache}
          showCustomPromptModal={showCustomPromptModal}
          setShowCustomPromptModal={setShowCustomPromptModal}
          canUserShare={() => canUserShare(selectedList)}
          onRenameItem={handleRenameItem}
          highlightedItems={highlightedItems}
          isOnline={isOnline}
        />
      );
    }

    return (
      <Dashboard
        lists={cache.lists}
        items={cache.items}
        onCreateList={handleCreateList}
        onDeleteList={handleDeleteList}
        onReorderLists={handleReorderLists}
        onSelectList={(list) => setSelectedListId(list.id)}
        listShares={listShares}
        onManageAccess={handleManageAccess}
        onShowAccountSettings={() => setShowAccountSettings(true)}
        user={user}
        isOnline={isOnline}
        lastOnlineAt={lastOnlineAt}
        pendingCount={pendingCount}
      />
    );
  };

  // Show a thin loading banner while we're booting auth or syncing while online.
  // This prevents the "blank logout flicker" by keeping cached UI visible.
  const showLoadingBanner = !!token && (!authResolved || (isOnline && !isFirebaseAuthenticated));

  return (
    <div className="App">
      <div className={`app-container ${theme}`}>
        {showLoadingBanner && (
          <div className="loading-banner" role="status" aria-live="polite">
            Loading…
          </div>
        )}
        {/* AI Status Notification */}
        {aiStatusNotification && (
          <div className={`ai-status-notification ${aiStatusNotification.type}`}>
            <span>{aiStatusNotification.message}</span>
            <button
              onClick={() => setAiStatusNotification(null)}
              className="notification-close"
            >
              ×
            </button>
          </div>
        )}

        {/* General Notification */}
        <Notification
          message={notification?.message}
          type={notification?.type}
          visible={!!notification}
          onClose={() => setNotification(null)}
        />

        {/* Environment Toggle (only visible in development) */}
        <EnvironmentToggle />

        {renderContent()}
      </div>
    </div>
  );
}

export default App;
